<script>
  import { auth, AUTH_ERRORS } from './providers/index.js'

  export let onLoginSuccess = () => {}

  let username = ''
  let password = ''
  let isLoading = false
  let errorMessage = ''
  let isRegistering = false

  async function handleAuth() {
    if (!username.trim() || !password.trim()) {
      errorMessage = '请输入用户名和密码'
      return
    }

    isLoading = true
    errorMessage = ''

    try {
      if (isRegistering) {
        // 注册逻辑
        const { data, error } = await auth.signUp(
          `${username}@example.com`,
          password,
          { username }
        )

        if (error) {
          throw error
        }

        // 注册成功后自动登录
        if (data.user) {
          await signIn()
        }
      } else {
        // 登录逻辑
        await signIn()
      }
    } catch (error) {
      console.error('认证错误:', error)
      
      if (error.message === AUTH_ERRORS.INVALID_CREDENTIALS) {
        errorMessage = '用户名或密码错误'
      } else if (error.message && error.message.includes('User already registered')) {
        errorMessage = '用户名已被注册'
      } else {
        errorMessage = error.message || '登录失败，请重试'
      }
    } finally {
      isLoading = false
    }
  }

  async function signIn() {
    const { data, error } = await auth.signIn(
      `${username}@example.com`,
      password
    )

    if (error) {
      throw error
    }

    if (data.user) {
      // 确保用户资料存在
      await auth.ensureUserProfile(data.user, username)
      onLoginSuccess(data.user)
    }
  }

  function handleKeyPress(event) {
    if (event.key === 'Enter') {
      handleAuth()
    }
  }

  function toggleMode() {
    isRegistering = !isRegistering
    errorMessage = ''
  }
</script>

<div class="login-container">
  <div class="login-card">
    <div class="login-header">
      <h1>{isRegistering ? '注册账户' : '登录'}</h1>
      <p class="subtitle">{isRegistering ? '创建新的账户' : '登录到您的待办事项'}</p>
    </div>

    <form class="login-form" on:submit|preventDefault={handleAuth}>
      <div class="form-group">
        <label for="username">用户名</label>
        <input
          id="username"
          type="text"
          bind:value={username}
          placeholder="请输入用户名"
          on:keypress={handleKeyPress}
          disabled={isLoading}
          required
        />
      </div>

      <div class="form-group">
        <label for="password">密码</label>
        <input
          id="password"
          type="password"
          bind:value={password}
          placeholder="请输入密码"
          on:keypress={handleKeyPress}
          disabled={isLoading}
          required
        />
      </div>

      {#if errorMessage}
        <div class="error-message">
          {errorMessage}
        </div>
      {/if}

      <button 
        type="submit" 
        class="login-button"
        disabled={isLoading}
      >
        {#if isLoading}
          <span class="spinner"></span>
          {isRegistering ? '注册中...' : '登录中...'}
        {:else}
          {isRegistering ? '注册' : '登录'}
        {/if}
      </button>
    </form>

    <div class="toggle-mode">
      <span>{isRegistering ? '已有账户？' : '没有账户？'}</span>
      <button 
        type="button" 
        class="link-button"
        on:click={toggleMode}
      >
        {isRegistering ? '立即登录' : '立即注册'}
      </button>
    </div>
  </div>
</div>

<style>
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
  }

  .login-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    padding: 40px;
    width: 100%;
    max-width: 400px;
  }

  .login-header {
    text-align: center;
    margin-bottom: 30px;
  }

  .login-header h1 {
    color: #333;
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  .subtitle {
    color: #666;
    font-size: 16px;
    margin: 0;
  }

  .login-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-group label {
    color: #333;
    font-weight: 500;
    font-size: 14px;
  }

  .form-group input {
    padding: 12px 16px;
    border: 2px solid #e1e5e9;
    border-radius: 8px;
    font-size: 16px;
    transition: border-color 0.2s;
  }

  .form-group input:focus {
    outline: none;
    border-color: #667eea;
  }

  .form-group input:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }

  .error-message {
    background-color: #fee;
    color: #c33;
    padding: 12px;
    border-radius: 6px;
    font-size: 14px;
    text-align: center;
  }

  .login-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 14px 20px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .login-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }

  .login-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .toggle-mode {
    text-align: center;
    margin-top: 24px;
    color: #666;
    font-size: 14px;
  }

  .link-button {
    background: none;
    border: none;
    color: #667eea;
    cursor: pointer;
    font-size: 14px;
    text-decoration: underline;
    padding: 0;
    margin-left: 4px;
  }

  .link-button:hover {
    color: #764ba2;
  }

  @media (max-width: 480px) {
    .login-container {
      padding: 10px;
    }

    .login-card {
      padding: 30px 20px;
    }

    .login-header h1 {
      font-size: 24px;
    }
  }
</style>